import { memo, useCallback, useMemo } from 'react';
import { Globe, Phone, Mail, MapPin, Zap, Edit3, Copy, Navigation } from 'lucide-react';
import {
  getDisplayRegion, formatWebsiteUrl, getWebsiteHref, openDirections
} from './MapHelpers';
import { safeHref } from '../../utils/url';
import { copyToClipboard } from '../../utils/clipboard';
import SourceLogo from './SourceLogo';

const BusinessPopup = memo(({ business, onSuggestEdit }) => {
  const displayRegion = useMemo(() => getDisplayRegion(business), [business]);
  const websiteHref = useMemo(() => safeHref(getWebsiteHref(business.website)), [business.website]);
  const osmHref = useMemo(() => safeHref(business.osm_url), [business.osm_url]);

  const copyAddress = useCallback((e) => {
    e.stopPropagation();
    copyToClipboard(displayRegion, 'Address copied!');
  }, [displayRegion]);

  const handleDirections = useCallback((e) => {
    e.stopPropagation();
    openDirections(business);
  }, [business]);

  return (
    <div className="p-2 min-w-[200px] max-w-[280px]">
      <div className="flex items-start gap-2 mb-2">
        <div className="flex flex-col items-center gap-1">
          <SourceLogo business={business} size="md" showAbibMember />
          {business.payment_lightning && (
            <Zap 
              className="w-4 h-4 text-yellow-500 fill-yellow-500 cursor-help" 
              title="Lightning Accepted"
            />
          )}
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-sm leading-tight">{business.business_name}</h3>
          {business.abib_member && !business.btc_map && (
            <span className="text-[10px] text-orange-600 font-medium">ABIB Member</span>
          )}
        </div>
      </div>
      
      <div className="space-y-1 text-xs">
        {/* Address row. Only show copy/directions for records with real
            coords — online OL records like Bitrefill have no address to
            copy and nowhere to navigate. */}
        <div className="flex items-start gap-1 text-gray-600">
          <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span className="flex-1 text-[11px]">{displayRegion}</span>
          {business.latitude && business.longitude && (
            <>
              <button
                onClick={copyAddress}
                className="p-1 hover:bg-gray-100 rounded"
                title="Copy address"
              >
                <Copy className="w-3 h-3 text-gray-400 hover:text-gray-600" />
              </button>
              <button
                onClick={handleDirections}
                className="p-1 hover:bg-gray-100 rounded"
                title="Get directions"
              >
                <Navigation className="w-3 h-3 text-blue-500 hover:text-blue-700" />
              </button>
            </>
          )}
        </div>
        
        {business.based_state && !business.btc_map && business.based_state !== business.region && (
          <p className="text-gray-500 text-[10px]">Based in: {business.based_state}</p>
        )}
        
        {/* Action buttons */}
        <div className="flex items-center gap-2 pt-1">
          {websiteHref && (
            <a
              href={websiteHref}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 bg-primary/10 hover:bg-primary/20 rounded-full transition-colors"
              title={formatWebsiteUrl(business.website)}
              onClick={(e) => e.stopPropagation()}
            >
              <Globe className="w-4 h-4 text-primary" />
            </a>
          )}
          
          {business.contact && !business.contact.includes('@') && (
            <a 
              href={`tel:${business.contact.replace(/\s/g, '')}`}
              className="p-1.5 bg-green-100 hover:bg-green-200 rounded-full transition-colors"
              title={business.contact}
              onClick={(e) => e.stopPropagation()}
            >
              <Phone className="w-4 h-4 text-green-600" />
            </a>
          )}
          
          {business.contact && business.contact.includes('@') && (
            <a 
              href={`mailto:${business.contact}`}
              className="p-1.5 bg-blue-100 hover:bg-blue-200 rounded-full transition-colors"
              title={business.contact}
              onClick={(e) => e.stopPropagation()}
            >
              <Mail className="w-4 h-4 text-blue-600" />
            </a>
          )}
          
          {business.payment_lightning && (
            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">
              <Zap className="w-3 h-3" /> LN
            </span>
          )}
          {business.payment_onchain && (
            <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">
              BTC
            </span>
          )}
        </div>
        
        {osmHref && (
          <a
            href={osmHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-gray-400 hover:text-primary hover:underline"
          >
            View on OpenStreetMap →
          </a>
        )}
        
        {business.notes && !business.btc_map && (
          <p className="text-gray-500 text-[10px] pt-1 border-t border-gray-100 mt-1">{business.notes}</p>
        )}
        
        {!business.btc_map && onSuggestEdit && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSuggestEdit(business);
            }}
            className="w-full mt-2 pt-2 border-t border-gray-100 text-[10px] text-primary hover:text-primary-hover flex items-center justify-center gap-1"
          >
            <Edit3 className="w-3 h-3" />
            Suggest an Edit
          </button>
        )}
      </div>
    </div>
  );
});

export default BusinessPopup;
